import { useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import type { TMe } from '@/src/features/auth/models/auth.types'
import type { TCommentApi } from '@/src/features/comment/api/types/commentApi.types'
import { useToast } from '@/src/general/components/ToastContext'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useTranslate } from '@/src/lang/useTranslate'

import { getCommentResponsesOptions, useCommentResponsesQuery } from '../services/useCommentResponsesQuery'
import { getCommentsOptions } from '../services/useCommentsQuery'
import { Comment } from './Comment'

type TPublicationCommentProps = {
  comment: TCommentApi
  me?: TMe
  publicationId: number
}

export const CommentThread = ({ comment, me, publicationId }: TPublicationCommentProps): ReactNode => {
  const [isResponseQueryEnabled, setIsResponseQueryEnabled] = useState(false)

  const { addToast } = useToast()
  const { t } = useTranslate()
  const { data, isPending } = useCommentResponsesQuery(comment.id, isResponseQueryEnabled)
  const queryClient = useQueryClient()

  const displayToast = () => {
    addToast({
      message: t('app.report-abuse.comment.success'),
      colors: 'success',
      iconName: ICON.MESSAGE_INFO,
    })
  }
  const onCommentReported = () => {
    displayToast()
    queryClient.setQueryData(getCommentsOptions(publicationId).queryKey, (prevData) =>
      prevData
        ? {
            ...prevData,
            pages: prevData.pages.map((comments) => comments.filter(({ id }) => id !== comment.id)),
          }
        : prevData,
    )
  }

  const onResponseReported = (responseId: number) => {
    displayToast()
    queryClient.setQueryData(getCommentResponsesOptions(comment.id).queryKey, (responses) =>
      responses?.filter(({ id }) => id !== responseId),
    )
    queryClient.setQueryData(
      getCommentsOptions(publicationId).queryKey,
      (prevData) =>
        prevData && {
          ...prevData,
          pages: prevData.pages.map((comments) =>
            comments.map((c) =>
              c.id !== comment.id ? c : { ...c, responses: c?.responses?.filter(({ id }) => id !== responseId) },
            ),
          ),
        },
    )
  }

  return (
    <View>
      <View className="h-4" />
      <Comment
        publicationId={publicationId}
        isHozanaAdmin={!!me?.admin}
        comment={comment}
        isMine={comment.user.id === me?.id}
        onCommentReported={comment.user.id === me?.id ? undefined : onCommentReported}
      />
      <View>
        {(data || comment.responses)?.map((response) => (
          <Comment
            publicationId={publicationId}
            isHozanaAdmin={!!me?.admin}
            className="mt-2"
            key={response.id}
            parentId={comment.id}
            comment={response}
            isMine={response.user.id === me?.id}
            onCommentReported={response.user.id === me?.id ? undefined : () => onResponseReported(response.id)}
          />
        ))}
        {!!(comment.responses && comment.responses.length > 1 && (!isResponseQueryEnabled || isPending)) && (
          <Button
            testID={TEST_ID.COMMENT.LOAD_RESPONSES}
            colors="default"
            variant="faded"
            className="my-4"
            onPress={() => setIsResponseQueryEnabled(true)}
            isLoading={!!(isResponseQueryEnabled && isPending)}
          >
            <FormattedMessage id="publication.comment.loadmoreanswers" />
          </Button>
        )}
      </View>
    </View>
  )
}
